import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import DefaultApiLimit from '../../../config/defaultLimit';
import currentTimeZone from '../../../config/timeZone';
import { CommonFilterProperties } from '../../../interface/filter.interface';
import {
  RequestOCPPLogsInterface,
  OCPPLogsInterface,
} from '../../../interface/ocpp-logs-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disableOCPPLogsFiltersApplied,
  fetchOCPPLogs,
  fetchOCPPLogsFilters,
  updateOCPPLogsFilters,
  setSortData,
  refreshOCPPLogs,
} from '../../../redux/slices/OcppLogSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import OcppLogSidePanel from '../../../shared-components/table-side-panel/OcppLogsSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
// import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import ConvertUTCtoHHMMSS from '../../../shared-functions/ConvertUTCtoHHMMSS';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
// import FormatUtcString from '../../../shared-functions/FormatUtcString';
import FormatUtcStringWithSeconds from '../../../shared-functions/FormatUtcStringWithSeconds';
import getInitialDateRange from '../../../shared-functions/GetInitialDateRange';
import ocppLogsApi from '../../../utils/api/ocpp-logs';

export interface OCPPLogsProperties {
  account?: string;
  chargingStation?: string;
  chargingPointSn?: string;
  autoRefreshEnabled?: boolean;
  disableAutoRefreshToggle?: boolean;
  hideAccountStationChargerFilters?: boolean;
  hideTabs?: boolean;
}

/**
 * Station activity component (OCPP Logs Table)
 * @param {OCPPLogsProperties} properties - Component properties
 * @returns {React.ReactElement} The rendered component
 */
function OcppLogsTable(properties: OCPPLogsProperties = {}): React.ReactElement {
  const {
    account,
    chargingStation,
    chargingPointSn,
    autoRefreshEnabled = false,
    hideAccountStationChargerFilters = false,
  } = properties;

  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() =>
    getInitialDateRange(),
  );
  const isInitialFetch = useRef(true);
  const previousDateRange = useRef(dateRange);

  /**
   * Clears the date range values
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters
   * @returns {React.ReactElement} The result
   */
  const renderDateFilters = (): React.ReactElement => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          disableDates="future"
          requiredTime
        />
      </div>
    );
  };

  const {
    ocppLogs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.ocppCPOLogs);
  const { selectedChargingPoint } = useAppSelector((state) => state.device);

  const targetChargingPointSn = chargingPointSn || selectedChargingPoint?.chargingPointSn;
  const targetChargingStation =
    chargingStation ||
    (selectedChargingPoint as any)?.chargingStationId ||
    (selectedChargingPoint as any)?.chargingStation ||
    (selectedChargingPoint as any)?.chargingStationName;
  const targetAccount =
    account ||
    (selectedChargingPoint as any)?.accountCustomId ||
    (selectedChargingPoint as any)?.accountName;
  /**
   * Get query parameters for the OCPP logs
   * @param activeFilters Active filters for the OCPP logs
   * @returns Query parameters for the OCPP logs
   */
  const getQueryParameters = (activeFilters: CommonFilterProperties[]) => {
    const queryParameters = GetParametersFromCommonFilters(activeFilters);
    if (targetChargingPointSn) {
      queryParameters.chargingPointSn = targetChargingPointSn;
    }
    if (targetChargingStation) {
      queryParameters.chargingStation = targetChargingStation;
    }
    if (targetAccount) {
      queryParameters.account = targetAccount;
    }
    if (dateRange.startDate) {
      queryParameters.startDate = dateRange.startDate;
      queryParameters.timeZone = currentTimeZone;
    }
    if (dateRange.endDate) {
      queryParameters.endDate = dateRange.endDate;
      if (!queryParameters.timeZone) queryParameters.timeZone = currentTimeZone;
    }
    return queryParameters;
  };

  const hiddenFilterKeys = [
    'account',
    'accountName',
    'accountCustomId',
    'cpo',
    'partner',
    'chargingStation',
    'chargingPointSn',
  ];

  const displayedFilters = useMemo(() => {
    if (!hideAccountStationChargerFilters) {
      return filters;
    }
    return filters.filter((filter) => !hiddenFilterKeys.includes(filter.key));
  }, [filters, hideAccountStationChargerFilters]);

  useEffect(() => {
    dispatch(setPageTitle(t('ocppLogs')));
    dispatch(fetchOCPPLogsFilters());
  }, [dispatch, t]);

  const column = useMemo(
    () => [
      {
        label: t('date'),
        accessorKey: 'created_at' as keyof OCPPLogsInterface,
        sortAllow: true,

        /**
         * Converting timestamp to UTC date
         * @param {OCPPLogsInterface} ocppLog - The log object
         * @returns {React.ReactElement} The rendered element
         */
        customData: (ocppLog: OCPPLogsInterface) => <p>{ConvertUTCtoDate(ocppLog.created_at)}</p>,
      },
      {
        label: t('time'),
        accessorKey: 'time' as keyof OCPPLogsInterface,
        /**
         * Converting timestamp to UTC time with seconds
         * @param {OCPPLogsInterface} ocppLogTime - The log object
         * @returns {React.ReactElement} The rendered element
         */
        customData: (ocppLogTime: OCPPLogsInterface) => (
          <p>{FormatUtcStringWithSeconds(ConvertUTCtoHHMMSS(ocppLogTime.created_at))}</p>
        ),
      },
      {
        label: t('type'),
        accessorKey: 'type' as keyof OCPPLogsInterface,
        sortAllow: false,

        /**
         * Converting timestamp to UTC time
         * @param {OCPPLogsInterface} ocppLog - The log object
         * @returns {React.ReactElement} The rendered element
         */
        customData: (ocppLog: OCPPLogsInterface) => {
          const type = ocppLog.type?.toLowerCase();

          return (
            <p className={`${type === 'in' ? 'type-in' : 'type-out'}`}>
              {type ? t(type as 'in' | 'out') : '-'}
            </p>
          );
        },
      },
      {
        label: t('messageType'),
        accessorKey: 'action' as keyof OCPPLogsInterface,
      },
      {
        label: t('message'),
        accessorKey: 'message' as keyof OCPPLogsInterface,
        columnWidth: 500,
      },
    ],
    [t],
  );

  /**
   * Handle the fetch the data in api function
   * @param {RequestOCPPLogsInterface} data The fetch params
   */
  const fetchData = (data: RequestOCPPLogsInterface) => {
    dispatch(
      fetchOCPPLogs({
        ...data,
      }),
    );
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'created_at' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('messageType'), accessorKey: 'action' },
    { label: t('message'), accessorKey: 'message' },
    // { label: t('connectorId'), accessorKey: 'connectorId' },
    // { label: t('meterStart'), accessorKey: 'meterStart' },
    // { label: t('meterStop'), accessorKey: 'meterStop' },
    // { label: t('ocppLogTransactionId'), accessorKey: 'transactionId' },
    // { label: t('status'), accessorKey: 'status' },
    // { label: t('errorCode'), accessorKey: 'errorCode' },
    // { label: t('outcome'), accessorKey: 'outcome' },
    // { label: t('remoteAddress'), accessorKey: 'remoteAddress' },
    // { label: t('forwardedFor'), accessorKey: 'forwardedFor' },
    // { label: t('negotiatedSubprotocol'), accessorKey: 'negotiatedSubprotocol' },
    // { label: t('closeCode'), accessorKey: 'code' },
    // { label: t('durationMs'), accessorKey: 'durationMs' },
    // { label: t('stack'), accessorKey: 'stack' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function for pdf and csv
   * @param {string} type pdf or csv
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = getQueryParameters(filters);
    setExportLoader(true);
    ocppLogsApi
      .fetchOCPPLogsExport({
        ...queryParameters,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: OCPPLogsInterface, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'created_at':
                return ConvertUTCtoDate(row?.created_at) || 'N/A';
              case 'time':
                return FormatUtcStringWithSeconds(ConvertUTCtoHHMMSS(row?.created_at)) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'OCPP Logs.csv');
        } else {
          const isRtl = i18n.dir() === 'rtl' || i18n.language === 'ar';
          const columnStyles: Record<number, any> = {};

          exportCurrentColumns.forEach((col, index) => {
            switch (col.accessorKey) {
              case '__serial__':
                columnStyles[index] = {
                  cellWidth: 32,
                  minCellWidth: 28,
                  halign: 'center',
                };
                break;
              case 'created_at':
                columnStyles[index] = {
                  cellWidth: 60,
                  minCellWidth: 50,
                  halign: 'center',
                };
                break;
              case 'time':
                columnStyles[index] = {
                  cellWidth: 52,
                  minCellWidth: 46,
                  halign: 'center',
                };
                break;
              case 'type':
                columnStyles[index] = {
                  cellWidth: 40,
                  minCellWidth: 35,
                  halign: 'center',
                };
                break;
              case 'action':
                columnStyles[index] = {
                  cellWidth: 70,
                  minCellWidth: 60,
                  halign: isRtl ? 'right' : 'left',
                };
                break;
              case 'message':
                columnStyles[index] = {
                  cellWidth: 'auto',
                  minCellWidth: 100,
                  halign: isRtl ? 'right' : 'left',
                };
                break;
              case 'transactionId':
                columnStyles[index] = {
                  cellWidth: 55,
                  minCellWidth: 50,
                  halign: 'center',
                };
                break;
              case 'errorCode':
                columnStyles[index] = {
                  cellWidth: 50,
                  minCellWidth: 45,
                  halign: 'center',
                };
                break;
              case 'outcome':
                columnStyles[index] = {
                  cellWidth: 50,
                  minCellWidth: 45,
                  halign: 'center',
                };
                break;
              case 'remoteAddress':
                columnStyles[index] = {
                  cellWidth: 60,
                  minCellWidth: 52,
                  halign: 'center',
                };
                break;
              case 'forwardedFor':
                columnStyles[index] = {
                  cellWidth: 55,
                  minCellWidth: 50,
                  halign: 'center',
                };
                break;
              case 'negotiatedSubprotocol':
                columnStyles[index] = {
                  cellWidth: 70,
                  minCellWidth: 60,
                  halign: 'center',
                };
                break;
              default:
                columnStyles[index] = {
                  cellWidth: 'auto',
                  minCellWidth: 45,
                  halign: isRtl ? 'right' : 'center',
                };
                break;
            }
          });

          exportPDF(headers, rows, 'OCPP Logs.pdf', isRtl, {
            columnStyles,
            isRtl,
          });
        }
        setExportLoader(false);

        if (totalResults > 1000) {
          dispatch(
            setNotificationMessage({
              message: t('exportCappedNotice', { totalResults }),
              status: true,
              type: 'success',
              code: 0,
            }),
          );
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value The page props
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = getQueryParameters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = getQueryParameters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function for Auto Refresh the data
   */
  const handleAutoRefresh = useCallback(() => {
    const queryParameters = getQueryParameters(filters);

    dispatch(
      refreshOCPPLogs({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  }, [
    dispatch,
    filters,
    limit,
    page,
    sortBy,
    sortField,
    targetAccount,
    targetChargingPointSn,
    targetChargingStation,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = getQueryParameters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to add search params, side panel will open automatically
   * @param {string} logId The log id
   */
  const handleOpenSidePanelOnRowClick = (logId: string) => {
    // Set or update the logId param
    searchParameters.set('logId', logId);

    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  useEffect(() => {
    // Trigger data fetch initially when filter list is loaded, or when user updates filters / date range
    const dateChanged =
      previousDateRange.current.startDate !== dateRange.startDate ||
      previousDateRange.current.endDate !== dateRange.endDate;
    previousDateRange.current = dateRange;

    const shouldFetchInitial = !filterListLoading && isInitialFetch.current;
    const shouldFetchUpdated = !isInitialFetch.current && (isFilterUpdated || dateChanged);

    if (shouldFetchInitial || shouldFetchUpdated) {
      if (shouldFetchInitial) {
        isInitialFetch.current = false;
      }
      const queryParameters = getQueryParameters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
      });
      if (isFilterUpdated) {
        dispatch(disableOCPPLogsFiltersApplied());
      }
    }
  }, [
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    limit,
    hideAccountStationChargerFilters,
    targetChargingPointSn,
    targetChargingStation,
    targetAccount,
    dateRange.startDate,
    dateRange.endDate,
  ]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('oCPPLogs'),
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="station-activity-wrap ocpp-logs-table-wrap" dir={i18n.dir()}>
      <div className="station-activity-table">
        <CustomTable
          version="v2"
          header={column}
          data={ocppLogs}
          serialNo
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={displayedFilters}
          filtersToDispatch={updateOCPPLogsFilters}
          filterListToDispatch={fetchOCPPLogsFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refershCurrentPages={handleRefresh}
          requiredRefreshToggle
          refreshInterval={15}
          refreshFunction={handleAutoRefresh}
          autoRefreshEnabled={autoRefreshEnabled}
          pageLimitChange={pageNateLimitChange}
          sortData={{
            sortBy,
            sortField,
          }}
          height={340}
          handleSortChange={handleSortFunction}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="logId"
          rowClickSidePanel={<OcppLogSidePanel fetchIdFrom="queryParams" />}
          onHiddenColumnsChange={setHiddenColumns}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
        />
      </div>
    </div>
  );
}

OcppLogsTable.defaultProps = {
  account: '',
  chargingStation: '',
  chargingPointSn: '',
  autoRefreshEnabled: false,
  disableAutoRefreshToggle: false,
  hideAccountStationChargerFilters: false,
  hideTabs: false,
};

export default OcppLogsTable;
